import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { RoleplayMode, RoleplaySessionResponse } from '@wundo/shared/roleplay.ts';
import { PracticeSessionBriefPanes } from './PracticeSessionBriefPanes.tsx';
import { PracticeSessionStart } from './PracticeSessionStart.tsx';
import type { LiveConversation } from './PracticeSessionLive.tsx';
import { PracticeVoiceSetup } from './PracticeVoiceSetup.tsx';
import { beginRoleplaySession } from '@/lib/roleplaySession.ts';
import type { AudioSetup } from '@/lib/useAudioSetup.ts';

export interface PracticeSessionBriefStageProps {
  session: RoleplaySessionResponse;
  /** The audio check between the brief and a spoken conversation is open. */
  checkingAudio: boolean;
  audio: AudioSetup;
  onCheckAudio: () => void;
  onLeaveAudioCheck: () => void;
  /** The session was granted the conversation: it is live from here on. */
  onBegun: (live: LiveConversation) => void;
}

/** Before the conversation: the brief, and how to start it — in voice or in chat. */
export function PracticeSessionBriefStage({
  session,
  checkingAudio,
  audio,
  onCheckAudio,
  onLeaveAudioCheck,
  onBegun,
}: PracticeSessionBriefStageProps) {
  const [mode, setMode] = useState<RoleplayMode>('voice');

  /*
   * The mode travels with the mutation rather than being read back from state:
   * "practise in chat instead" switches it and starts in the same click.
   */
  const begin = useMutation({
    mutationFn: async (chosen: RoleplayMode) => beginRoleplaySession(session.id, chosen),
    onSuccess: (voice, chosen) => onBegun({ mode: chosen, voice }),
  });

  return (
    <div className='flex min-h-0 flex-1 flex-col px-5 pt-6.5 pb-6.5'>
      <div className='mx-auto flex min-h-0 w-full max-w-240 flex-1 flex-col gap-4.5'>
        <PracticeSessionBriefPanes scenario={session.scenario} />

        {/*
         * Outside the panes' scroll, so the start stays on screen however
         * long the brief is — on a phone too, where the panes stack.
         */}
        <div className='shrink-0'>
          {checkingAudio ? (
            <PracticeVoiceSetup
              audio={audio}
              starting={begin.isPending}
              failed={begin.isError}
              onStart={() => begin.mutate('voice')}
              onBack={onLeaveAudioCheck}
              onChatInstead={() => {
                setMode('chat');
                begin.mutate('chat');
              }}
            />
          ) : (
            <PracticeSessionStart
              mode={mode}
              starting={begin.isPending}
              failed={begin.isError}
              onModeChange={setMode}
              onBegin={() => (mode === 'voice' ? onCheckAudio() : begin.mutate('chat'))}
            />
          )}
        </div>
      </div>
    </div>
  );
}
